import { DeleteOutlineOutlined } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import ReplayIcon from '@mui/icons-material/Replay';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Typography,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

type ModalView = 'upload' | 'processing' | 'complete' | 'error';

interface ImportError {
  row: number;
  message: string;
}

interface ImportResponse {
  message: string;
  total: number;
  success: number;
  failed: number;
  errors: ImportError[];
}

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  onProceed: (file: File) => Promise<ImportResponse>;
}

const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: 700 },
  bgcolor: 'background.paper',
  borderRadius: '12px',
  boxShadow: 24,
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
};

const iconContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: 'text.secondary',
};

const UploadView: React.FC<{
  onFileSelect: (file: File | null) => void;
  selectedFile: File | null;
}> = ({ onFileSelect, selectedFile }) => {
  const { t } = useTranslation();
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
      }
    },
    [onFileSelect]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
    },
    maxSize: 10 * 1024 * 1024, // 10 MB
  });

  const dropzoneStyle = useMemo(
    () => ({
      flex: 1,
      display: 'flex',
      flexDirection: 'column' as 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px',
      borderWidth: 2,
      borderRadius: '8px',
      borderColor: isDragActive ? 'primary.main' : 'divider',
      borderStyle: 'dashed',
      backgroundColor: 'customBackground.section',
      color: 'text.secondary',
      outline: 'none',
      transition: 'border .24s ease-in-out, background-color .24s ease-in-out',
      cursor: 'pointer',
      textAlign: 'center' as 'center',
      height: '250px',
    }),
    [isDragActive]
  );

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFileSelect(null);
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
        {t('upload_file_2')}
      </Typography>
      <Box {...getRootProps({ sx: dropzoneStyle })}>
        <input {...getInputProps()} />
        {selectedFile ? (
          <Fade in={true}>
            <Box sx={{ textAlign: 'center' }}>
              <UploadFileOutlinedIcon
                color="primary"
                sx={{ fontSize: 48, mb: 1.5 }}
              />
              <Typography
                variant="body1"
                color="text.primary"
                fontWeight="medium"
              >
                {selectedFile.name}
              </Typography>
              <Typography variant="caption" display="block" mb={2}>
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
              <Button
                onClick={handleRemoveFile}
                size="small"
                startIcon={<DeleteOutlineOutlined />}
                variant="outlined"
                color="error"
              >
                Remove
              </Button>
            </Box>
          </Fade>
        ) : (
          <Box>
            <UploadFileOutlinedIcon
              sx={{ fontSize: 48, mb: 1.5, color: 'text.secondary' }}
            />
            <Typography variant="body1" color="text.primary">
              {t('drag_your_file_or')}{' '}
              <span style={{ color: '#3B82F6', fontWeight: 'medium' }}>
                {t('click_to_upload')}
              </span>
            </Typography>
            <Typography variant="caption">
              {t('supports_xlsx_only_up_to_10mb')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const ProcessingView: React.FC = () => (
  <Box sx={{ textAlign: 'center', py: 8, height: '285px' }}>
    <Box sx={iconContainerStyle}>
      <CircularProgress />
    </Box>
    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
      Processing your file
    </Typography>
    <Typography color="text.secondary">
      Please wait while we process your file.
    </Typography>
  </Box>
);

const CompleteView: React.FC<{ rowsAdded: number }> = ({ rowsAdded }) => (
  <Box sx={{ textAlign: 'center', py: 8, height: '285px' }}>
    <Box sx={{ ...iconContainerStyle, color: 'success.main' }}>
      <CheckCircleIcon sx={{ fontSize: 64 }} />
    </Box>
    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
      Import Complete
    </Typography>
    <Typography color="text.secondary">
      We have successfully imported {rowsAdded} rows from your file.
    </Typography>
  </Box>
);

const ErrorView: React.FC<{
  errors: ImportError[];
  successCount: number;
  failedCount: number;
}> = ({ errors, successCount, failedCount }) => (
  <Box sx={{ py: 2 }}>
    <Box
      sx={{
        ...iconContainerStyle,
        color: 'error.main',
        flexDirection: 'column',
        textAlign: 'center',
      }}
    >
      <ErrorIcon sx={{ fontSize: 64 }} />
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        {failedCount > 0 && successCount > 0
          ? 'Import Partially Complete'
          : 'Import Failed'}
      </Typography>
      <Typography color="text.secondary">
        {successCount} rows imported successfully. {failedCount} rows failed.
      </Typography>
      <Typography color="text.secondary">
        Please check the errors below:
      </Typography>
    </Box>
    <List
      sx={{
        maxHeight: '240px',
        overflowY: 'auto',
        bgcolor: 'action.hover',
        borderRadius: 2,
        p: 1,
      }}
    >
      {errors.map((err, index) => (
        <ListItem key={index} disableGutters>
          <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5 }}>
            <Chip label={`Row ${err.row}`} size="small" />
          </ListItemIcon>
          <ListItemText
            primary={err.message}
            primaryTypographyProps={{
              variant: 'body2',
              color: 'text.secondary',
            }}
          />
        </ListItem>
      ))}
    </List>
  </Box>
);

export const ImportModal: React.FC<ImportModalProps> = ({
  open,
  onClose,
  onProceed,
}) => {
  const { t } = useTranslation();
  const [view, setView] = useState<ModalView>('upload');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errorData, setErrorData] = useState<ImportError[]>([]);
  const [successRows, setSuccessRows] = useState(0);
  const [failedRows, setFailedRows] = useState(0);

  const resetState = (newView: ModalView = 'upload') => {
    setView(newView);
    setSelectedFile(null);
    setErrorData([]);
    setSuccessRows(0);
    setFailedRows(0);
  };

  const handleClose = () => {
    if (isLoading) return;
    resetState();
    onClose();
  };

  const handleReupload = () => {
    resetState('upload');
  };

  const handleProceed = async () => {
    if (!selectedFile) return;
    if (selectedFile.size <= 0) return;

    try {
      setIsLoading(true);
      setView('processing');
      const response = await onProceed(selectedFile);

      if (response.total === response.success && response.failed === 0) {
        setSuccessRows(response.success || 0);
        setView('complete');
      } else {
        setErrorData(response?.errors || []);
        setSuccessRows(response?.success || 0);
        setFailedRows(response?.failed || 0);
        setView('error');
      }
    } catch (error) {
      console.error('Import failed:', error);
      const errorResponse = (error as any)?.response?.data;
      if (errorResponse?.errors?.length) {
        setErrorData(errorResponse.errors);
        setSuccessRows(errorResponse.success || 0);
        setFailedRows(errorResponse.failed || 0);
      } else {
        setErrorData([
          {
            row: 0,
            message:
              errorResponse?.message ||
              'An unexpected error occurred during processing.',
          },
        ]);
      }
      setView('error');
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    switch (view) {
      case 'upload':
        return (
          <UploadView
            onFileSelect={setSelectedFile}
            selectedFile={selectedFile}
          />
        );
      case 'processing':
        return <ProcessingView />;
      case 'complete':
        return <CompleteView rowsAdded={successRows} />;
      case 'error':
        return (
          <ErrorView
            errors={errorData}
            successCount={successRows}
            failedCount={failedRows}
          />
        );
      default:
        return null;
    }
  };

  const renderButtons = () => {
    switch (view) {
      case 'upload':
        return (
          <>
            <Button variant="text" onClick={handleClose}>
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleProceed}
              disabled={!selectedFile || isLoading}
            >
              {t('cdp_proceed')}
            </Button>
          </>
        );
      case 'processing':
        return (
          <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
            {t('cancel')}
          </Button>
        );
      case 'complete':
        return (
          <Button variant="contained" onClick={handleClose}>
            {t('close')}
          </Button>
        );
      case 'error':
        return (
          <>
            <Button
              variant="outlined"
              onClick={handleReupload}
              startIcon={<ReplayIcon />}
            >
              {t('re_upload')}
            </Button>
            <Button variant="contained" onClick={handleClose}>
              {t('close')}
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Modal open={open} onClose={handleClose} closeAfterTransition>
      <Fade in={open}>
        <Box sx={modalStyle}>
          <Box sx={{ p: 2, pb: 0 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">
                {t('boundary_importer_btn_import')}
              </Typography>
              <IconButton onClick={handleClose} disabled={isLoading}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Divider />
          <Box sx={{ p: 3, pt: 1 }}>{renderContent()}</Box>
          <Divider />
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, p: 2 }}
          >
            {renderButtons()}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};
